// src/lib/topScrollbars.ts
//
// Adds a second, synchronized horizontal scrollbar ABOVE every wide data table
// so columns can be scrolled without first scrolling to the bottom of the
// table. Purely additive and global — no per-table markup changes.
//
// How it works: for each <table> on the page we find its nearest horizontally
// scrollable ancestor (the `overflow-x-auto` container). When that container
// overflows, we inject a thin scrollbar strip as the container's previous
// sibling and keep the two scroll positions in sync.
//
// Re-scan / re-measure triggers (belt-and-suspenders, since ResizeObserver and
// requestAnimationFrame are throttled/paused in background tabs):
//   - MutationObserver on <body> (pages/data render)          → structural
//   - ResizeObserver per container+table (foreground)         → layout
//   - window 'resize'                                         → layout
//   - custom 'geni:remeasure' event (fired by the text-size   → deterministic
//     setting in displayPrefs) so scaling always re-measures.
//
// Only elements that contain a real <table> are enhanced, so horizontal pill
// strips / chip rows (which also use overflow-x-auto) are left alone.

export const REMEASURE_EVENT = 'geni:remeasure';

interface Controller {
  bar: HTMLDivElement;
  ro: ResizeObserver;
  measure: () => void;
  detach: () => void;
}

const MARK = 'data-topscroll';

function findScrollContainer(table: HTMLElement): HTMLElement | null {
  let el: HTMLElement | null = table.parentElement;
  while (el && el !== document.body) {
    const ox = getComputedStyle(el).overflowX;
    if (ox === 'auto' || ox === 'scroll') return el;
    el = el.parentElement;
  }
  return null;
}

function enhance(container: HTMLElement, registry: Map<HTMLElement, Controller>) {
  if (container.getAttribute(MARK) === '1' || !container.parentElement) return;

  const bar = document.createElement('div');
  bar.setAttribute(MARK, 'bar');
  bar.style.overflowX = 'auto';
  bar.style.overflowY = 'hidden';
  bar.style.height = '12px';
  bar.style.marginBottom = '4px';
  bar.style.maxWidth = '100%';
  bar.style.display = 'none';

  const spacer = document.createElement('div');
  spacer.style.height = '1px';
  spacer.style.width = '0px';
  bar.appendChild(spacer);

  try {
    container.parentElement.insertBefore(bar, container);
  } catch {
    return;
  }
  container.setAttribute(MARK, '1');

  // Value-compare scroll sync: writing the same scrollLeft fires no scroll
  // event, and a >1px guard stops sub-pixel ping-pong — so no lock flag can
  // get stuck and drop a sync.
  const onBarScroll = () => {
    if (Math.abs(container.scrollLeft - bar.scrollLeft) > 1) container.scrollLeft = bar.scrollLeft;
  };
  const onContainerScroll = () => {
    if (Math.abs(bar.scrollLeft - container.scrollLeft) > 1) bar.scrollLeft = container.scrollLeft;
  };
  bar.addEventListener('scroll', onBarScroll, { passive: true });
  container.addEventListener('scroll', onContainerScroll, { passive: true });

  const measure = () => {
    const sw = container.scrollWidth;
    const cw = container.clientWidth;
    const overflowing = sw - cw > 1;
    bar.style.display = overflowing ? 'block' : 'none';
    if (overflowing) {
      bar.style.width = cw + 'px';
      spacer.style.width = sw + 'px';
      if (Math.abs(bar.scrollLeft - container.scrollLeft) > 1) bar.scrollLeft = container.scrollLeft;
    }
  };

  const ro = new ResizeObserver(measure);
  ro.observe(container);
  const tbl = container.querySelector('table');
  if (tbl) ro.observe(tbl);

  const detach = () => {
    try {
      bar.removeEventListener('scroll', onBarScroll);
      container.removeEventListener('scroll', onContainerScroll);
      ro.disconnect();
      bar.remove();
      container.removeAttribute(MARK);
    } catch {
      /* ignore */
    }
  };

  registry.set(container, { bar, ro, measure, detach });
  measure();
}

let started = false;

/** Start the global top-scrollbar enhancer. Returns a cleanup function. */
export function initTopScrollbars(): () => void {
  if (started || typeof document === 'undefined') return () => {};
  started = true;

  const registry = new Map<HTMLElement, Controller>();

  const scan = () => {
    // Enhance any new tables.
    document.querySelectorAll('table').forEach((t) => {
      const container = findScrollContainer(t as HTMLElement);
      if (container && container.getAttribute(MARK) !== '1') {
        enhance(container, registry);
      }
    });
    // Re-measure existing bars and drop any whose container left the DOM.
    registry.forEach((ctrl, container) => {
      if (!document.body.contains(container)) {
        ctrl.detach();
        registry.delete(container);
      } else {
        ctrl.measure();
      }
    });
  };

  // setTimeout (not rAF) so it still runs in background/inactive tabs.
  let timer: ReturnType<typeof setTimeout> | 0 = 0;
  const schedule = () => {
    if (timer) return;
    timer = setTimeout(() => {
      timer = 0;
      scan();
    }, 80);
  };

  const mo = new MutationObserver((mutations) => {
    for (const m of mutations) {
      const t = m.target as HTMLElement;
      if (t?.getAttribute?.(MARK) === 'bar') continue; // ignore our own churn
      schedule();
      break;
    }
  });
  mo.observe(document.body, { childList: true, subtree: true });
  window.addEventListener('resize', schedule, { passive: true });
  window.addEventListener(REMEASURE_EVENT, schedule);

  scan();

  return () => {
    mo.disconnect();
    window.removeEventListener('resize', schedule);
    window.removeEventListener(REMEASURE_EVENT, schedule);
    if (timer) clearTimeout(timer);
    registry.forEach((ctrl) => ctrl.detach());
    registry.clear();
    started = false;
  };
}
